/**
 * Sign-up.
 *
 * Deliberately does NOT ask for subjects. It used to, and the tickboxes were
 * the longest thing on the form for something that is optional, reversible,
 * and available in one click afterwards from Add a Subject — which records the
 * chosen teacher exactly as the sign-up form did, so nothing about enrolment
 * is lost by moving it. Registration now asks only for what an account cannot
 * exist without, plus the one question that has to be settled before any
 * answer is written: whether it may be sent to the AI marker.
 */
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ApiError } from "../api/client";
import { AuthLayout } from "../components/AuthLayout";
import { AiConsentChoice, AiConsentExplainer } from "../components/AiConsentChoice";

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", name: "", password: "", email: "" });
  // Null until they answer. Neither radio starts selected, so agreement is
  // never the default and the form cannot be submitted without a real answer.
  const [aiConsent, setAiConsent] = useState<boolean | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (aiConsent === null) {
      setError("Please choose how your answers should be marked.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await register({ ...form, ai_consent: aiConsent });
      // Straight to the catalogue rather than to My Subjects, which would be
      // empty by definition for an account created a second ago. Picking a
      // subject is the only thing there is to do next, so the redirect does it
      // instead of making them find the way.
      navigate("/browse", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Registration failed");
    } finally {
      setLoading(false);
    }
  }

  function set(key: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));
  }

  return (
    <AuthLayout
      title="Create your account"
      intro="Takes a minute. You choose your subjects on the next screen, and can add or drop them whenever you like."
      footer={
        <span className="auth-footer-alt">
          Already have an account? <Link to="/login">Sign in</Link>
        </span>
      }
    >
      <form onSubmit={handleSubmit} className="auth-form">
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
        <label>
          <span className="field-label">Full name</span>
          <input
            value={form.name}
            onChange={set("name")}
            autoComplete="name"
            autoFocus
            required
          />
        </label>
        <label>
          <span className="field-label">Username</span>
          <input
            value={form.username}
            onChange={set("username")}
            autoComplete="username"
            required
          />
        </label>
        <label>
          <span className="field-label">
            Email <span className="field-optional">optional</span>
          </span>
          <input
            type="email"
            value={form.email}
            onChange={set("email")}
            autoComplete="email"
          />
          <span className="field-hint">
            The only way to reset your password if you forget it.
          </span>
        </label>
        <label>
          <span className="field-label">Password</span>
          <input
            type="password"
            value={form.password}
            onChange={set("password")}
            autoComplete="new-password"
            minLength={6}
            required
          />
          <span className="field-hint">At least 6 characters.</span>
        </label>

        {/* Asked before the account exists, not after the first lesson — by
            then the answers have already been sent. Required: there is no
            "decide later" option here, because "later" is mid-dialogue. */}
        <fieldset className="consent-pick">
          <legend className="field-label">How your answers are marked</legend>
          <AiConsentExplainer />
          <AiConsentChoice
            value={aiConsent}
            onChange={setAiConsent}
            name="register-ai-consent"
            disabled={loading}
          />
        </fieldset>

        <button
          type="submit"
          className="btn btn-primary btn-lg"
          disabled={loading || aiConsent === null}
        >
          {loading ? "Creating account…" : "Create account"}
        </button>
      </form>
    </AuthLayout>
  );
}
